import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Styled from './CountdownTimer.styled';
import { calculateTimeRemaining, formatTimeUnit } from './utils';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The `CountdownTimer` content block displays a countdown timer to a specific end date.
 */
export const CountdownTimer = ({
  endDate,
  countdownLabel,
  ...rest
}) => {
  const labelId = React.useId();
  const {
    t
  } = useTranslation('blocks.countdownTimer');
  const [timeRemaining, setTimeRemaining] = React.useState(() => {
    if (!endDate) {
      return {
        days: 0,
        hours: 0,
        minutes: 0
      };
    }
    return calculateTimeRemaining(endDate);
  });
  React.useEffect(() => {
    if (!endDate) {
      return () => {};
    }

    // Update immediately on mount
    setTimeRemaining(calculateTimeRemaining(endDate));

    // Set up interval to update every minute
    const intervalId = setInterval(() => {
      const next = calculateTimeRemaining(endDate);
      setTimeRemaining(next);
      if (next.days === 0 && next.hours === 0 && next.minutes === 0) {
        clearInterval(intervalId);
      }
    }, 60000);

    // Cleanup interval on unmount
    return () => {
      clearInterval(intervalId);
    };
  }, [endDate]);

  // Don't render if the timer has run out
  if (timeRemaining.days === 0 && timeRemaining.hours === 0 && timeRemaining.minutes === 0) {
    return null;
  }
  return /*#__PURE__*/_jsx(Styled.CountdownTimerStandaloneContainer, {
    "data-id": "cp-countdown-timer",
    children: /*#__PURE__*/_jsxs(Styled.CountdownTimerContainer, {
      ...rest,
      children: [/*#__PURE__*/_jsx(Styled.Label, {
        id: labelId,
        children: countdownLabel
      }), /*#__PURE__*/_jsxs(Styled.TimerDisplay, {
        role: "timer",
        "aria-atomic": "true",
        "aria-describedby": labelId,
        children: [/*#__PURE__*/_jsxs(Styled.TimeUnit, {
          children: [/*#__PURE__*/_jsx(Styled.TimeValue, {
            children: formatTimeUnit(timeRemaining.days)
          }), /*#__PURE__*/_jsx(Styled.TimeLabel, {
            children: t('CountdownTimer.timeUnit.days', 'Days')
          })]
        }), /*#__PURE__*/_jsxs(Styled.TimeUnit, {
          children: [/*#__PURE__*/_jsx(Styled.TimeValue, {
            children: formatTimeUnit(timeRemaining.hours)
          }), /*#__PURE__*/_jsx(Styled.TimeLabel, {
            children: t('CountdownTimer.timeUnit.hours', 'Hours')
          })]
        }), /*#__PURE__*/_jsxs(Styled.TimeUnit, {
          children: [/*#__PURE__*/_jsx(Styled.TimeValue, {
            children: formatTimeUnit(timeRemaining.minutes)
          }), /*#__PURE__*/_jsx(Styled.TimeLabel, {
            children: t('CountdownTimer.timeUnit.minutes', 'Minutes')
          })]
        })]
      })]
    })
  });
};